import { describe, it, expect, vi, beforeEach, afterEach } from 'vitest';
import KubernetesResourceReservationHelper from 'Kubernetes/helpers/resourceReservationHelper';
import KubernetesCreateApplicationController from './createApplicationController.js';

// Mock Angular
vi.mock('angular', () => ({
  default: {
    module: vi.fn(() => ({
      controller: vi.fn(),
    })),
  },
  module: vi.fn(() => ({
    controller: vi.fn(),
  })),
}));

// Mock global angular
global.angular = {
  module: vi.fn(() => ({
    controller: vi.fn(),
  })),
};

// Mock only network-related dependencies
vi.mock('@/react/portainer/settings/settings.service', () => ({
  getGlobalDeploymentOptions: vi.fn(),
}));

vi.mock('@/react/kubernetes/ingresses/service', () => ({
  updateIngress: vi.fn(),
  getIngresses: vi.fn(),
}));

vi.mock('@/react/portainer/environments/environment.service', () => ({
  getDeploymentOptions: vi.fn(),
}));

vi.mock('@/react/kubernetes/services/useNamespaceServices', () => ({
  getServices: vi.fn(),
}));

vi.mock('@/react/kubernetes/namespaces/queries/useNamespacesQuery', () => ({
  getNamespaces: vi.fn(),
}));

vi.mock('Kubernetes/helpers/resourceReservationHelper', () => ({
  default: {
    megaBytesValue: vi.fn((bytes) => Math.round(bytes / (1024 * 1024))),
    bytesValue: vi.fn((megabytes) => megabytes * 1024 * 1024),
  },
}));

describe('KubernetesCreateApplicationController (Real Class)', () => {
  let controller;
  let mockDependencies;

  beforeEach(() => {
    vi.clearAllMocks();

    // Create mock dependencies for the constructor
    mockDependencies = {
      $scope: {
        $watch: vi.fn(),
      },
      $async: vi.fn((fn) => fn()),
      $state: { params: {} },
      $timeout: vi.fn(),
      $window: {},
      Notifications: {
        success: vi.fn(),
        error: vi.fn(),
      },
      Authentication: {
        getUserDetails: vi.fn(() => ({ username: 'testuser' })),
        hasAuthorizations: vi.fn(() => false),
      },
      KubernetesResourcePoolService: {},
      KubernetesApplicationService: {},
      KubernetesStackService: {},
      KubernetesConfigurationService: {},
      KubernetesNodeService: {},
      KubernetesIngressService: {},
      KubernetesPersistentVolumeClaimService: {},
      KubernetesVolumeService: {},
      RegistryService: {},
      StackService: {},
      KubernetesNodesLimitsService: {},
      EndpointProvider: {
        endpointID: { Id: 1 },
      },
      EndpointService: {},
      StateManager: {},
    };

    // Instantiate the real controller
    controller = new KubernetesCreateApplicationController(
      mockDependencies.$scope,
      mockDependencies.$async,
      mockDependencies.$state,
      mockDependencies.$timeout,
      mockDependencies.$window,
      mockDependencies.Notifications,
      mockDependencies.Authentication,
      mockDependencies.KubernetesResourcePoolService,
      mockDependencies.KubernetesApplicationService,
      mockDependencies.KubernetesStackService,
      mockDependencies.KubernetesConfigurationService,
      mockDependencies.KubernetesNodeService,
      mockDependencies.KubernetesIngressService,
      mockDependencies.KubernetesPersistentVolumeClaimService,
      mockDependencies.KubernetesVolumeService,
      mockDependencies.RegistryService,
      mockDependencies.StackService,
      mockDependencies.KubernetesNodesLimitsService,
      mockDependencies.EndpointProvider,
      mockDependencies.EndpointService,
      mockDependencies.StateManager
    );

    // Set up some test data
    controller.nodesLimits = {
      MaxCPU: 4,
      MaxMemory: 8 * 1024 * 1024 * 1024, // 8GB in bytes
      overflowForReplica: vi.fn(() => false),
      overflowForGlobal: vi.fn(() => false),
    };

    controller.nodeNumber = 3;
  });

  afterEach(() => {
    vi.clearAllMocks();
  });

  describe('resourceQuotaCapacityExceeded', () => {
    it('returns true when any slider max is 0 or falsy', () => {
      controller.state.sliders.memory.max = 0;
      controller.state.sliders.memoryRequest.max = 1024;
      controller.state.sliders.cpu.max = 2;
      controller.state.sliders.cpuRequest.max = 1;

      expect(controller.resourceQuotaCapacityExceeded()).toBe(true);
    });

    it('returns false when all slider maxes are greater than 0', () => {
      controller.state.sliders.memory.max = 1024;
      controller.state.sliders.memoryRequest.max = 512;
      controller.state.sliders.cpu.max = 2;
      controller.state.sliders.cpuRequest.max = 1;

      expect(controller.resourceQuotaCapacityExceeded()).toBe(false);
    });

    it('returns true when cpuRequest max is 0', () => {
      controller.state.sliders.memory.max = 1024;
      controller.state.sliders.memoryRequest.max = 512;
      controller.state.sliders.cpu.max = 2;
      controller.state.sliders.cpuRequest.max = 0;

      expect(controller.resourceQuotaCapacityExceeded()).toBe(true);
    });

    it('returns true when memoryRequest max is 0', () => {
      controller.state.sliders.memory.max = 1024;
      controller.state.sliders.memoryRequest.max = 0;
      controller.state.sliders.cpu.max = 2;
      controller.state.sliders.cpuRequest.max = 1;

      expect(controller.resourceQuotaCapacityExceeded()).toBe(true);
    });
  });

  describe('effectiveInstances', () => {
    it('returns replica count for replicated deployment', () => {
      controller.formValues.DeploymentType = 'Replicated';
      controller.formValues.ReplicaCount = 3;
      controller.nodeNumber = 5;

      expect(controller.effectiveInstances()).toBe(3);
    });

    it('returns node number for global deployment', () => {
      controller.formValues.DeploymentType = 'Global';
      controller.formValues.ReplicaCount = 3;
      controller.nodeNumber = 5;

      expect(controller.effectiveInstances()).toBe(5);
    });
  });

  describe('resourceReservationsOverflow', () => {
    beforeEach(() => {
      // Set up reasonable limits
      controller.state.namespaceLimits.cpu = 4;
      controller.state.namespaceLimits.memory = 4 * 1024 * 1024 * 1024; // 4GB
      controller.state.namespaceRequests.cpu = 2;
      controller.state.namespaceRequests.memory = 2 * 1024 * 1024 * 1024; // 2GB

      // Set up form values
      controller.formValues.CpuLimit = 0.5;
      controller.formValues.CpuRequest = 0.2;
      controller.formValues.MemoryLimit = 256; // 256MB
      controller.formValues.MemoryRequest = 128; // 128MB
      controller.formValues.ReplicaCount = 2;
      controller.formValues.DeploymentType = 'Replicated';
    });

    it('returns false when resources are within limits', () => {
      expect(controller.resourceReservationsOverflow()).toBe(false);
    });

    it('returns true when CPU limit exceeds namespace limit', () => {
      controller.formValues.CpuLimit = 3; // 3 * 2 instances = 6 > 4 (namespace limit)

      expect(controller.resourceReservationsOverflow()).toBe(true);
    });

    it('returns true when CPU request exceeds namespace request', () => {
      controller.formValues.CpuRequest = 1.5; // 1.5 * 2 instances = 3 > 2 (namespace request)

      expect(controller.resourceReservationsOverflow()).toBe(true);
    });

    it('returns true when memory limit exceeds namespace limit', () => {
      controller.formValues.MemoryLimit = 3072; // 3GB * 2 instances = 6GB > 4GB (namespace limit)

      expect(controller.resourceReservationsOverflow()).toBe(true);
    });

    it('returns true when memory request exceeds namespace request', () => {
      controller.formValues.MemoryRequest = 1536; // 1.5GB * 2 instances = 3GB > 2GB (namespace request)

      expect(controller.resourceReservationsOverflow()).toBe(true);
    });

    it('calls nodesLimits.overflowForReplica for replicated deployment', () => {
      controller.formValues.DeploymentType = 'Replicated';
      controller.formValues.ReplicaCount = 3;

      controller.resourceReservationsOverflow();

      expect(controller.nodesLimits.overflowForReplica).toHaveBeenCalledWith(
        controller.formValues.CpuRequest,
        expect.any(Number), // memory request in bytes
        3
      );
    });

    it('calls nodesLimits.overflowForGlobal for global deployment', () => {
      controller.formValues.DeploymentType = 'Global';

      controller.resourceReservationsOverflow();

      expect(controller.nodesLimits.overflowForGlobal).toHaveBeenCalledWith(
        controller.formValues.CpuRequest,
        expect.any(Number) // memory request in bytes
      );
    });
  });

  describe('autoScalerOverflow', () => {
    beforeEach(() => {
      controller.state.namespaceLimits.cpu = 4;
      controller.state.namespaceLimits.memory = 4 * 1024 * 1024 * 1024; // 4GB
      controller.state.namespaceRequests.cpu = 2;
      controller.state.namespaceRequests.memory = 2 * 1024 * 1024 * 1024; // 2GB

      controller.formValues.AutoScaler.maxReplicas = 3;
      controller.formValues.CpuLimit = 0.5;
      controller.formValues.CpuRequest = 0.2;
      controller.formValues.MemoryLimit = 256;
      controller.formValues.MemoryRequest = 128;
    });

    it('returns false when autoscaler resources are within limits', () => {
      expect(controller.autoScalerOverflow()).toBe(false);
    });

    it('returns true when autoscaler CPU limit would exceed namespace limit', () => {
      controller.formValues.AutoScaler.maxReplicas = 5;
      controller.formValues.CpuLimit = 1; // 1 * 5 instances = 5 > 4 (namespace limit)

      expect(controller.autoScalerOverflow()).toBe(true);
    });

    it('returns true when autoscaler CPU request would exceed namespace request', () => {
      controller.formValues.AutoScaler.maxReplicas = 5;
      controller.formValues.CpuRequest = 0.5; // 0.5 * 5 instances = 2.5 > 2 (namespace request)

      expect(controller.autoScalerOverflow()).toBe(true);
    });
  });

  describe('updateSliders', () => {
    beforeEach(() => {
      controller.state.namespaceLimits.cpu = 4;
      controller.state.namespaceLimits.memory = 4 * 1024 * 1024 * 1024; // 4GB in bytes
      controller.state.namespaceRequests.cpu = 2;
      controller.state.namespaceRequests.memory = 2 * 1024 * 1024 * 1024; // 2GB in bytes
    });

    it('sets minimum values when quota exists', () => {
      const namespaceWithQuota = {
        Quota: {
          CpuLimit: 2,
          CpuRequest: 1,
          MemoryLimit: 1024 * 1024 * 1024, // 1GB
          MemoryRequest: 512 * 1024 * 1024, // 512MB
        },
      };

      controller.updateSliders(namespaceWithQuota);

      expect(controller.state.sliders.cpu.min).toBe(0.1);
      expect(controller.state.sliders.cpuRequest.min).toBe(0.1);
      // Memory minimums will be calculated by the helper function
      expect(KubernetesResourceReservationHelper.megaBytesValue).toHaveBeenCalled();
    });

    it('sets maximum values correctly considering node limits', () => {
      const namespaceWithQuota = {
        Quota: {
          CpuLimit: 2,
          CpuRequest: 1,
          MemoryLimit: 1024 * 1024 * 1024, // 1GB
          MemoryRequest: 512 * 1024 * 1024, // 512MB
        },
      };

      controller.updateSliders(namespaceWithQuota);

      // Should be minimum of namespace limit and node limit
      expect(controller.state.sliders.cpu.max).toBe(4); // Math.min(4, 4)
      expect(controller.state.sliders.cpuRequest.max).toBe(2); // Math.min(2, 4)
    });

    it('handles no quota scenario', () => {
      const namespaceWithQuota = {
        Quota: null,
      };

      controller.updateSliders(namespaceWithQuota);

      expect(controller.state.sliders.cpu.min).toBe(0);
      expect(controller.state.sliders.cpuRequest.min).toBe(0);
      expect(controller.state.sliders.memory.min).toBe(0);
      expect(controller.state.sliders.memoryRequest.min).toBe(0);
    });

    it('handles case where max is less than min for memory', () => {
      controller.state.namespaceLimits.memory = 64 * 1024 * 1024; // 64MB - less than default minimum
      controller.state.namespaceRequests.memory = 32 * 1024 * 1024; // 32MB - less than default minimum

      const namespaceWithQuota = {
        Quota: {
          MemoryLimit: 1024 * 1024 * 1024, // Has quota, so min will be 128MB
          MemoryRequest: 1024 * 1024 * 1024,
        },
      };

      controller.updateSliders(namespaceWithQuota);

      // When max < min, both should be set to 0
      expect(controller.state.sliders.memory.max).toBe(0);
      expect(controller.state.sliders.memory.min).toBe(0);
      expect(controller.state.sliders.memoryRequest.max).toBe(0);
      expect(controller.state.sliders.memoryRequest.min).toBe(0);
    });
  });

  describe('Real Controller Integration', () => {
    it('should have all the expected properties initialized', () => {
      expect(controller.state).toBeDefined();
      expect(controller.formValues).toBeDefined();
      expect(controller.ApplicationDeploymentTypes).toBeDefined();
      expect(controller.state.sliders).toBeDefined();
      expect(controller.state.sliders.cpuRequest).toBeDefined();
      expect(controller.state.sliders.memoryRequest).toBeDefined();
      expect(controller.state.namespaceLimits).toBeDefined();
      expect(controller.state.namespaceRequests).toBeDefined();
    });

    it('should have the new cpuRequest and memoryRequest sliders initialized', () => {
      expect(controller.state.sliders.cpuRequest.min).toBe(0);
      expect(controller.state.sliders.cpuRequest.max).toBe(0);
      expect(controller.state.sliders.memoryRequest.min).toBe(0);
      expect(controller.state.sliders.memoryRequest.max).toBe(0);
    });

    it('should have the new namespace request limits initialized', () => {
      expect(controller.state.namespaceRequests.cpu).toBe(0);
      expect(controller.state.namespaceRequests.memory).toBe(0);
    });
  });
});
